import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShieldCheck, BadgeCheck, AlertTriangle } from "lucide-react";
import PillarPageLayout from "./PillarPageLayout";
import AuthorByline from "@/components/AuthorByline";

const FAQ = [
  {
    q: "¿Cuánto cuesta Immunocal en México en 2026?",
    a: "El precio público de Immunocal Original (Azul) es aproximadamente $1,849 MXN, Immunocal Platinum $2,449 MXN e Immunocal Sport $2,559 MXN por caja de 30 sobres. A través del Kit de Inicio Immunotec puedes acceder a un descuento de hasta 42%, lo que reduce el costo significativamente y mantiene ese precio en todas tus compras posteriores.",
  },
  {
    q: "¿Por qué Immunocal es más caro que otras proteínas de suero?",
    a: "Porque NO es la misma proteína. Immunocal se procesa a baja temperatura para preservar enlaces disulfuro bioactivos que permiten al cuerpo producir glutatión. Las whey comunes pasan por calor y químicos que desnaturalizan esas proteínas. Estás pagando por funcionalidad, no por gramos de proteína cruda.",
  },
  {
    q: "¿Hay precio diferente en farmacias o supermercados?",
    a: "Immunocal NO se vende en farmacias ni supermercados. Es venta directa exclusiva a través de Distribuidores Independientes Immunotec. Si lo ves en Mercado Libre o Amazon con precios extraños, ten cuidado: podría ser producto vencido, recargado o falsificado.",
  },
  {
    q: "¿Cómo identifico Immunocal original?",
    a: "Verifica: (1) caja sellada con holograma Immunotec, (2) lote y fecha de caducidad legibles, (3) sobres con sellado térmico perfecto, (4) polvo blanco fino sin grumos ni olor extraño, (5) número de afiliado de tu distribuidor en la factura. Ante duda, llama al servicio oficial de Immunotec.",
  },
  {
    q: "¿Vale la pena el Kit de Inicio si solo voy a probar?",
    a: "Sí, casi siempre. El Kit de Inicio te da el primer producto + acceso al precio de afiliado de por vida. Aunque solo compres una caja al año, ya recuperaste la diferencia. Y si decides continuar, ahorras miles de pesos en el tiempo.",
  },
];

const RELATED = [
  { label: "Guía completa de Immunocal", href: "/immunocal", desc: "Beneficios, ingredientes, cómo tomarlo." },
  { label: "Glutatión: antioxidante maestro", href: "/glutation", desc: "Por qué Immunocal eleva el glutatión." },
  { label: "Blog Eliora", href: "/blog", desc: "Más artículos del universo Immunotec." },
];

export default function ImmunocalPrecioPage(props) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <Helmet>
        <title>Immunocal Precio México 2026: Cuánto Cuesta y Dónde Comprar Original</title>
        <meta name="description" content="Precio Immunocal México 2026: Original $1,849, Platinum $2,449, Sport $2,559. Hasta 42% de descuento con Kit de Inicio Immunotec. Cómo identificar producto original." />
        <link rel="canonical" href="https://elioralongevity.com/immunocal-precio" />
        <meta property="og:title" content="Immunocal Precio México 2026: Original, Platinum y Sport" />
        <meta property="og:description" content="Tabla de precios actualizada + descuento de hasta 42% con Kit de Inicio." />
        <meta property="og:url" content="https://elioralongevity.com/immunocal-precio" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map(({ q, a }) => ({
            "@type": "Question", name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        })}</script>
      </Helmet>

      <PillarPageLayout
        {...props}
        meta={{
          eyebrow: "Guía de compra · Actualizada 2026",
          h1: "Immunocal Precio México 2026: cuánto cuesta y dónde comprar original",
          lead: "Precios públicos vigentes, descuento real con Kit de Inicio, presentaciones y cómo asegurarte de que el producto que recibes es 100% original.",
        }}
        breadcrumbs={[{ label: "Inicio", href: "/" }, { label: "Immunocal precio" }]}
        faq={FAQ}
        relatedLinks={RELATED}
      >
        <AuthorByline date="2026-05-30" minutes={5} />

        <h2>Tabla de precios Immunocal · México 2026</h2>
        <div className="not-prose overflow-x-auto -mx-6 md:mx-0 my-6">
          <table className="w-full text-sm border-collapse bg-white rounded-2xl overflow-hidden shadow-sm">
            <thead>
              <tr className="bg-[#2c4a3b] text-[#f9f6f0]">
                <th className="text-left p-4 font-semibold">Presentación</th>
                <th className="text-right p-4 font-semibold">Precio público</th>
                <th className="text-right p-4 font-semibold">Con Kit de Inicio (42% off)</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[#2c4a3b]/10">
                <td className="p-4">
                  <strong>Immunocal Original (Azul)</strong>
                  <p className="text-xs text-[#5c6b61] mt-1">30 sobres · 10g c/u</p>
                </td>
                <td className="p-4 text-right text-[#5c6b61]">$1,849 MXN</td>
                <td className="p-4 text-right">
                  <span className="text-[#1a2421] font-bold text-lg">$1,072 MXN</span>
                  <p className="text-xs text-emerald-600 font-semibold">ahorras $777</p>
                </td>
              </tr>
              <tr className="border-b border-[#2c4a3b]/10 bg-[#bc6c25]/5">
                <td className="p-4">
                  <strong>Immunocal Platinum</strong>
                  <p className="text-xs text-[#5c6b61] mt-1">30 sobres · ideal +40 años</p>
                </td>
                <td className="p-4 text-right text-[#5c6b61]">$2,449 MXN</td>
                <td className="p-4 text-right">
                  <span className="text-[#1a2421] font-bold text-lg">$1,420 MXN</span>
                  <p className="text-xs text-emerald-600 font-semibold">ahorras $1,029</p>
                </td>
              </tr>
              <tr>
                <td className="p-4">
                  <strong>Immunocal Sport</strong>
                  <p className="text-xs text-[#5c6b61] mt-1">30 sobres · atletas y deportistas</p>
                </td>
                <td className="p-4 text-right text-[#5c6b61]">$2,559 MXN</td>
                <td className="p-4 text-right">
                  <span className="text-[#1a2421] font-bold text-lg">$1,484 MXN</span>
                  <p className="text-xs text-emerald-600 font-semibold">ahorras $1,075</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs italic text-[#5c6b61]">* Precios referenciales 2026 sujetos a cambios de Immunotec. Confirma precio vigente al momento de compra.</p>

        <h2>¿Qué es el Kit de Inicio y por qué es la mejor opción?</h2>
        <p>El <strong>Kit de Inicio Immunotec</strong> es la forma oficial de la empresa para que nuevos clientes accedan a sus productos al <strong>precio de Asociado Independiente</strong> — el mismo descuento que tiene un distribuidor activo. Incluye:</p>
        <ul>
          <li>Tu primer producto (Immunocal Original, Platinum o Sport, tú eliges).</li>
          <li>Acceso al precio con descuento permanente para todas tus compras futuras.</li>
          <li>Página personal para hacer pedidos directamente.</li>
          <li>No tienes obligación de vender ni de mantener volúmenes mínimos.</li>
        </ul>

        <h2>Cómo identificar Immunocal 100% original</h2>
        <div className="not-prose my-6 grid sm:grid-cols-2 gap-4">
          {[
            { icon: BadgeCheck, label: "Holograma Immunotec", text: "Sello holográfico oficial en la caja, no copiable." },
            { icon: ShieldCheck, label: "Sobres sellados térmicamente", text: "Sin fisuras, sin polvo afuera." },
            { icon: BadgeCheck, label: "Lote y caducidad legibles", text: "Fechas en relieve, nunca en stickers pegados." },
            { icon: AlertTriangle, label: "Cuidado en marketplaces", text: "Mercado Libre y Amazon NO son canales oficiales." },
          ].map((c, i) => (
            <div key={i} className="bg-white border border-[#2c4a3b]/8 rounded-2xl p-5">
              <div className="w-10 h-10 rounded-xl bg-[#bc6c25]/10 grid place-items-center text-[#bc6c25] mb-3"><c.icon className="w-5 h-5" /></div>
              <p className="font-semibold text-[#1a2421] text-sm">{c.label}</p>
              <p className="text-xs text-[#5c6b61] mt-1">{c.text}</p>
            </div>
          ))}
        </div>

        <h2>¿Dónde NO comprar Immunocal?</h2>
        <ul>
          <li>❌ Mercado Libre, Amazon, mercados de pulgas — riesgo de producto vencido, recargado o falsificado.</li>
          <li>❌ Farmacias y supermercados — Immunocal NO se vende en retail tradicional.</li>
          <li>❌ Distribuidores no verificados o sin factura — sin garantía de cadena de frío y origen.</li>
        </ul>

        <h2>Cómo obtener Immunocal con descuento conmigo</h2>
        <ol>
          <li>Haz el <Link to="/#quiz">quiz wellness gratuito</Link> (3 minutos).</li>
          <li>Recibes un protocolo personalizado de 3 meses.</li>
          <li>Te escribo por WhatsApp para revisar contigo cuál presentación te conviene.</li>
          <li>Te ayudo a registrarte con el Kit de Inicio y acceder al 42% de descuento.</li>
          <li>Recibes tu producto en tu casa (envío Immunotec oficial, con factura).</li>
        </ol>
      </PillarPageLayout>
    </>
  );
}
